/**
 * AppBroker - Class decoupling band-aid
 *
 * There are two tenets of this app's design which are at odds with each
 * other: a prevalent base class that provides a number of services
 * (DocumentStore), and a prevalent singleton that also provides a number of
 * services (RootStore). DocumentStore reaches out to RootStore to implement
 * a number of its services, and RootStore is itself a subclass of
 * DocumentStore, so we have a dependency cycle.
 *
 * We can get by as long as these import cycles don't actually lead to any
 * evaluation cycles, but avoiding that is a touchy, brittle process and
 * extremely hard to reason about, so it's worth at least trying to break
 * the higher-traffic edges in the dependency graph.
 *
 * This class provides a Broker, or a message bus, or whatever you want to
 * call it - a relatively neutral, low-dependency meeting point where
 * handlers can register their ability to service requests, and other
 * entities can make said requests, without either class having to know
 * about each other. It's sort of like event listeners in that an object can
 * call subscriber functions without knowing about who they are, only here
 * the 'listeners' can actually send responses back by returning values.
 *
 * This is still not a scalable solution - it's just a bit of duct tape. You
 * can observe that scaling this would be a challenge to balance with the
 * goals of having few dependencies, as some of the data objects have been
 * pared down to bare minimum interfaces, providing only the information
 * known to be required by consumers at the time of writing. A proper
 * refactor would impose a stricter top-down hierarchy where values travel
 * down through assignment and travel up through callbacks. However, it can
 * offer some insight into what abstractions might make sense if we ever
 * refactor this thing properly.
 */
import { type DocumentType } from 'types/DocumentType';
import { type SiteRole } from 'types/Roles';

export interface StarredFacade {
    // https://youtu.be/0S43IwBF0uM

    // The odd return type is because implementations are known to only
    // check for presence (an object) or absence (undefined)
    get(doc: { path: string }): object | undefined;

    add(doc: { path: string }): Promise<void>;
    remove(doc: { path: string }): Promise<void>;
}

export interface UserFacade {
    role: SiteRole;
    id: string;
    placeholderName: string;
    authenticated: boolean;
    starred: StarredFacade;
}

export interface RouteForDocumentOptions {
    documentTypeHint?: DocumentType;
}

export interface NavigateOptions extends RouteForDocumentOptions {
    keepQueryString?: boolean;
}

// Note that every query must be able to return some sort of 'zero' value
// (undefined, null, empty string, literal 0 etc) because a handler might
// not be registered.
export interface Queries {
    getCurrentUser(): UserFacade | null;

    /**
     * Navigates to the URL for viewing the given path parameters and
     * document type.
     *
     * In most cases the correct route can be determined by `pathParams`
     * alone, in which case `documentTypeHint` can be omitted. It is only
     * really necessary if you know in advance that a document path for a
     * creative is actually an animation.
     */
    navigate(
        pathParams: Record<string, string>,
        options?: NavigateOptions
    ): Promise<void>;

    /**
     * Performs navigation logic after logging in. This is used for
     * navigating to the default route *if* the user was on the login page.
     * Thus, implementers may decline to perform the navigation.
     */
    navigateAfterLogin(): Promise<void>;

    /**
     * Returns the pathname for viewing the given path parameters and
     * document type.
     */
    routeForDocument(
        pathParams: Record<string, string>,
        options?: RouteForDocumentOptions
    ): string;

    /**
     * Perform actions after the user logs out.
     */
    afterLogout(): void;
}

export type QueryTag = keyof Queries;

export interface AppBroker extends Queries {
    registerHandler<T extends QueryTag>(tag: T, handler: Queries[T]): AppBroker;
}

class AppBrokerImpl implements AppBroker {
    private readonly handlers: Queries = {
        getCurrentUser: () => null,
        navigate: () => Promise.resolve(undefined),
        navigateAfterLogin: () => Promise.resolve(undefined),
        routeForDocument: () => '',
        afterLogout: () => {},
    };
    private readonly registeredHandlers = new Set<QueryTag>();

    registerHandler<T extends QueryTag>(
        tag: T,
        handler: Queries[T]
    ): AppBroker {
        if (this.registeredHandlers.has(tag)) {
            throw new Error(`Handler for ${tag} already registered`);
        }
        this.handlers[tag] = handler;
        this.registeredHandlers.add(tag);
        return this;
    }

    getCurrentUser(): UserFacade | null {
        const { getCurrentUser } = this.handlers;
        return getCurrentUser();
    }

    navigate(
        pathParams: Record<string, string>,
        options?: NavigateOptions
    ): Promise<void> {
        const { navigate } = this.handlers;
        return navigate(pathParams, options);
    }

    routeForDocument(
        pathParams: Record<string, string>,
        options?: RouteForDocumentOptions
    ): string {
        const { routeForDocument } = this.handlers;
        return routeForDocument(pathParams, options);
    }

    navigateAfterLogin(): Promise<void> {
        const { navigateAfterLogin } = this.handlers;
        return navigateAfterLogin();
    }

    afterLogout(): void {
        const { afterLogout } = this.handlers;
        afterLogout();
    }
}

let appBrokerInstance: AppBroker | null = null;

export function getAppBroker(): AppBroker {
    if (!appBrokerInstance) {
        appBrokerInstance = new AppBrokerImpl();
    }
    return appBrokerInstance;
}
